import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { skyFlightFrame } from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import {
  skyLeviathanGeometry,
  skyLeviathanWalls,
  skyLeviathanMachinery,
  skyLeviathanVestibule,
  skyLeviathanArticulation,
  skyLeviathanRupture,
  SKY_LEVIATHAN_CANNON_MOUNTS,
} from '../../../packages/domain/src/95-sky-leviathan-space.mjs';
import { SKY_LEVIATHAN_ARMOR_PARTS } from '../../../packages/domain/src/97-sky-leviathan-armor.mjs';
import { batchSkyLeviathanInterior } from './176-sky-leviathan-batching.mjs';

const finitePoint = (point) => ['x', 'y', 'z'].every((axis) => Number.isFinite(point?.[axis]));

/** Imported metre-scale artwork only. Domain94/95 own positions, apertures and
 * damage; this factory samples authored joints and maps those exact coordinates
 * onto named parts. Missing assets/contracts fail instead of substituting a boss. */
export function createSkyLeviathanArt(T, models, resources) {
  const instances = new Map();
  const matrix = new T.Matrix4(),
    point = new T.Vector3(),
    end = new T.Vector3();
  const axis = new T.Vector3(),
    direction = new T.Vector3(),
    parentInverse = new T.Matrix4(),
    jointRotation = new T.Euler(0, 0, 0, 'XYZ'),
    jointDelta = new T.Quaternion(),
    warmMetal = new T.Color(0xffa34b),
    hotMetal = new T.Color(0xff761a);
  function own(record, value) {
    record.owned.add(value);
    resources.add(value);
    return value;
  }
  function basis(group, frame) {
    const valid = skyFlightFrame(frame);
    if (!valid) throw new Error('Leviathan artwork requires its canonical hull frame.');
    const { right: r, up: u, back: b, origin } = valid;
    matrix.set(r.x, u.x, b.x, 0, r.y, u.y, b.y, 0, r.z, u.z, b.z, 0, 0, 0, 0, 1);
    matrix.setPosition(origin.x, origin.y, origin.z);
    if (group.parent) {
      group.parent.updateWorldMatrix(true, false);
      matrix.premultiply(parentInverse.copy(group.parent.matrixWorld).invert());
    }
    matrix.decompose(group.position, group.quaternion, group.scale);
    // Descendants are updated after their own canonical joints are posed.
    // Rewalking the entire hull/skeleton here repeats work with stale poses.
    group.updateWorldMatrix(false, false);
  }
  function required(root, name) {
    const node = root.getObjectByName(name);
    if (!node) throw new Error(`Leviathan artwork missing named part: ${name}`);
    return node;
  }
  function materials(record, root) {
    const result = [];
    root.traverse((node) => {
      if (!node.isMesh) return;
      const original = Array.isArray(node.material) ? node.material : [node.material];
      const copies = original.map((source) => {
        const material = own(record, source.clone());
        result.push({
          material,
          color: material.color.clone(),
          emissive: material.emissive.clone(),
          glow: material.emissiveIntensity ?? 0,
        });
        return material;
      });
      node.material = Array.isArray(node.material) ? copies : copies[0];
    });
    return result;
  }
  function batchTarget(record, root) {
    root.updateWorldMatrix(true, true);
    const inverse = root.matrixWorld.clone().invert(),
      bins = new Map();
    root.traverse((node) => {
      if (!node.isMesh) return;
      if (
        node.isSkinnedMesh ||
        Array.isArray(node.material) ||
        node.children.length ||
        Object.keys(node.geometry.morphAttributes).length ||
        node.geometry.groups.length
      )
        throw new Error('Leviathan target batching requires rigid single-material leaf meshes.');
      if (!bins.has(node.material)) bins.set(node.material, []);
      bins.get(node.material).push(node);
    });
    for (const [material, nodes] of bins) {
      if (nodes.length < 2) continue;
      const copies = [];
      try {
        for (const node of nodes) {
          const copy = node.geometry.clone();
          copies.push(copy);
          copy.applyMatrix4(inverse.clone().multiply(node.matrixWorld));
        }
        const merged = mergeGeometries(copies, false);
        if (!merged) throw new Error('Leviathan target detail geometry could not be batched.');
        own(record, merged);
        merged.computeBoundingBox();
        merged.computeBoundingSphere();
        const mesh = new T.Mesh(merged, material);
        mesh.name = `${root.name}-batched-details`;
        mesh.castShadow = nodes[0].castShadow;
        mesh.receiveShadow = nodes[0].receiveShadow;
        mesh.renderOrder = nodes[0].renderOrder;
        root.add(mesh);
        for (const node of nodes) node.removeFromParent();
      } finally {
        for (const geometry of copies) geometry.dispose();
      }
    }
  }
  function tint(entries, exposed, disabled, charge = 0) {
    for (const { material, color, glow } of entries) {
      material.color.copy(color).multiplyScalar(disabled ? 0.28 : 1);
      material.emissiveIntensity = disabled ? 0 : glow * (exposed ? 1.2 : 0.25) + charge * 0.35;
    }
  }
  function heat(entries, charge) {
    const amount = T.MathUtils.clamp(charge ?? 0, 0, 1);
    for (const { material, color, emissive, glow } of entries) {
      material.color.copy(color).lerp(warmMetal, amount * 0.16);
      material.emissive.copy(emissive).lerp(hotMetal, amount * 0.85);
      material.emissiveIntensity = glow + amount * 1.1;
    }
  }
  function makeDolphin(record) {
    const asset = models.dolphin;
    if (!asset) throw new Error('Clockwork dolphin artwork is not prepared.');
    const model = cloneSkeleton(asset.scene),
      group = new T.Group();
    group.name = 'clockwork-dolphin-instance';
    // The pinned dolphin export faces -Z; the whale hull's nose is +Z. Apply
    // this fixed asset-basis correction before posing/recentring its real lens,
    // rather than turning the body toward the camera or a presentation target.
    model.rotateY(Math.PI);
    group.add(model);
    model.traverse((node) => {
      if (node.isSkinnedMesh) own(record, node.skeleton);
    });
    const rest = [];
    model.traverse((node) => {
      if (node.isBone)
        rest.push({ node, position: node.position.clone(), quaternion: node.quaternion.clone() });
    });
    const socket = required(model, 'socket_navigation_capsule');
    const capsule = required(model, 'navigation_capsule');
    const clip = asset.animations.find((item) => item.name === 'Swim');
    if (!clip || !(clip.duration > 0))
      throw new Error('Clockwork dolphin requires its authored Swim clip.');
    const mixer = new T.AnimationMixer(model);
    const action = mixer.clipAction(clip).play();
    action.paused = true;
    record.mixers.push({ mixer, model });
    const palette = materials(record, capsule);
    const lens = model.getObjectByName('navigation_control_lens');
    if (lens) palette.push(...materials(record, lens));
    return {
      group,
      model,
      socket,
      capsule,
      lens,
      mixer,
      action,
      duration: clip.duration,
      palette,
      rest,
    };
  }
  function sampleDolphin(dolphin, item, frame, age, reduced, crashPose = 0) {
    if (!finitePoint(item)) throw new Error('Dolphin artwork requires a finite navigation socket.');
    dolphin.action.time = reduced
      ? 0
      : ((age % dolphin.duration) + dolphin.duration) % dolphin.duration;
    dolphin.mixer.update(0);
    for (const bone of dolphin.rest) {
      bone.node.position.lerp(bone.position, crashPose);
      bone.node.quaternion.slerp(bone.quaternion, crashPose);
    }
    // The contact centre is the actual animated pod socket, not body origin.
    // Translate the complete four-metre rig; never resize it to its target.
    basis(dolphin.group, { ...frame, origin: item });
    dolphin.socket.getWorldPosition(point);
    end.set(item.x, item.y, item.z);
    if (dolphin.group.parent) {
      dolphin.group.parent.worldToLocal(point);
      dolphin.group.parent.worldToLocal(end);
    }
    dolphin.group.position.add(end.sub(point));
    dolphin.group.updateMatrixWorld(true);
    tint(dolphin.palette, Boolean(item.vulnerable), item.disabled);
  }
  function release(group) {
    const record = instances.get(group);
    if (!record) return;
    instances.delete(group);
    for (const { mixer, model } of record.mixers) {
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
    }
    for (const resource of record.owned) {
      resources.delete(resource);
      resource.dispose();
    }
    record.owned.clear();
  }
  function recordFor(group) {
    const record = { owned: new Set(), mixers: [] };
    instances.set(group, record);
    return record;
  }
  function createDolphin() {
    const owner = new T.Group(),
      record = recordFor(owner);
    try {
      const dolphin = makeDolphin(record);
      owner.add(dolphin.group);
      owner.userData.skyDolphin = dolphin;
      return owner;
    } catch (error) {
      release(owner);
      throw error;
    }
  }
  function prepareLining(record, group) {
    const root = required(record.model, 'curved_bronze_throat_vestibule');
    const open = skyLeviathanVestibule({ jaw: 1 });
    const vertices = open.flatMap((triangle, index) =>
      ['a', 'b', 'c'].map((key) => ({ index, key, point: triangle[key] })),
    );
    const meshes = [];
    const triangleKey = (points) =>
      points
        .map((p) => [p.x, p.y, p.z].map((v) => Math.round(v * 1e6)).join(','))
        .sort()
        .join('|');
    const expected = new Map();
    for (const triangle of open) {
      const key = triangleKey([triangle.a, triangle.b, triangle.c]);
      expected.set(key, (expected.get(key) ?? 0) + 1);
    }
    group.updateMatrixWorld(true);
    root.traverse((node) => {
      if (!node.isMesh) return;
      const positions = node.geometry.attributes.position;
      const mapping = [];
      for (let i = 0; i < positions.count; i++) {
        point.fromBufferAttribute(positions, i).applyMatrix4(node.matrixWorld);
        group.worldToLocal(point);
        const closest = vertices.reduce(
          (best, item) => {
            const distance = point.distanceToSquared(item.point);
            return distance < best.distance ? { ...item, distance } : best;
          },
          { distance: Infinity },
        );
        if (closest.distance > 1e-8)
          throw new Error(
            'Leviathan mouth lining does not match its canonical fully-open surface.',
          );
        mapping.push({ index: closest.index, key: closest.key });
      }
      const indices = node.geometry.index;
      for (let i = 0; i < (indices?.count ?? positions.count); i += 3) {
        const key = triangleKey(
          [0, 1, 2].map((offset) => {
            const mapped = mapping[indices ? indices.getX(i + offset) : i + offset];
            return open[mapped.index][mapped.key];
          }),
        );
        if (!expected.get(key))
          throw new Error('Leviathan mouth topology differs from its physical surface.');
        expected.set(key, expected.get(key) - 1);
      }
      node.geometry = own(record, node.geometry.clone());
      node.geometry.attributes.position.setUsage(T.DynamicDrawUsage);
      meshes.push({
        node,
        mapping,
        hullToGeometry: node.matrixWorld.clone().invert().multiply(group.matrixWorld),
      });
    });
    if (!meshes.length) throw new Error('Leviathan requires its visible physical mouth lining.');
    if ([...expected.values()].some(Boolean))
      throw new Error('Leviathan mouth artwork omits a physical facet.');
    return meshes;
  }
  function sampleLining(record, systems) {
    if (record.lastJaw === systems.jaw) return;
    const facets = skyLeviathanVestibule(systems);
    for (const { node, mapping, hullToGeometry } of record.lining) {
      const positions = node.geometry.attributes.position;
      mapping.forEach(({ index, key }, vertex) => {
        const current = facets[index][key];
        point.set(current.x, current.y, current.z).applyMatrix4(hullToGeometry);
        positions.setXYZ(vertex, point.x, point.y, point.z);
      });
      positions.needsUpdate = true;
      node.geometry.computeVertexNormals();
      node.geometry.computeBoundingBox();
      node.geometry.computeBoundingSphere();
    }
    record.lastJaw = systems.jaw;
  }
  function create() {
    if (!models.gildedLeviathan) throw new Error('Gilded Leviathan artwork is not prepared.');
    const group = new T.Group(),
      record = recordFor(group);
    group.userData.skyLeviathan = true;
    try {
      record.model = models.gildedLeviathan.scene.clone(true);
      group.add(record.model);
      record.root = required(record.model, 'gilded_leviathan');
      record.armorJoints = SKY_LEVIATHAN_ARMOR_PARTS.map(({ id, node: name }) => {
        const node = required(record.model, name);
        return { id, node, rest: node.quaternion.clone() };
      });
      record.fragments = new Map(
        skyLeviathanRupture({ breach: 0 }).map((part) => {
          const node = required(record.model, `${part.id}_hinge`);
          return [part.id, node];
        }),
      );
      record.cannons = SKY_LEVIATHAN_CANNON_MOUNTS.map((mount) => {
        const { side, base, length, restForward } = mount;
        const pivot = required(record.model, `cannon_${side}_pivot`),
          socket = required(record.model, `socket_cannon_${side}`);
        pivot.getWorldPosition(point);
        group.worldToLocal(point);
        socket.getWorldPosition(end);
        pivot.worldToLocal(end);
        if (
          point.distanceToSquared(base) > 1e-8 ||
          end.distanceToSquared(
            new T.Vector3(restForward.x, restForward.y, restForward.z).multiplyScalar(length),
          ) > 1e-8
        )
          throw new Error('Leviathan cannon artwork does not match its actual mount and muzzle.');
        return { side, pivot, socket, palette: materials(record, pivot) };
      });
      record.tailPalette = ['tail_01_surface', 'tail_02_surface', 'tail_03_surface'].flatMap(
        (name) => materials(record, required(record.model, name)),
      );
      record.lining = prepareLining(record, group);
      record.jaw = required(record.model, 'jaw_pivot');
      record.plug = required(record.model, 'pressure_plug');
      record.plugBase = record.plug.position.clone();
      record.plugClosed = skyLeviathanWalls({ jaw: 0, breach: 0 }).find(
        (wall) => wall.id === 'pressure-plug',
      ).center;
      record.tails = ['tail_01', 'tail_02', 'tail_03'].map((name) => {
        const node = required(record.model, name);
        return { node, base: node.rotation.clone() };
      });
      record.fins = ['port', 'starboard'].map((side) => {
        const node = required(record.model, `fin_${side}_pivot`);
        return { node, side, base: node.rotation.clone() };
      });
      record.colliders = new Map();
      const boxes = [
        ...skyLeviathanWalls({ jaw: 0, breach: 0 }),
        ...skyLeviathanMachinery(0).flatMap((gate) => gate.boxes),
      ];
      record.model.traverse((node) => {
        if (!node.userData.skyColliderId) return;
        const box = boxes.find((item) => item.id === node.userData.skyColliderId);
        if (!box)
          throw new Error(`Unknown authored Leviathan collider: ${node.userData.skyColliderId}`);
        record.colliders.set(box.id, { node, scale: node.scale.clone(), halfX: box.halfExtents.x });
      });
      for (const box of boxes)
        if (!record.colliders.has(box.id))
          throw new Error(`Missing Leviathan collider artwork: ${box.id}`);
      record.parts = skyLeviathanGeometry()
        .components.filter((part) => part.kind !== 'navigation')
        .map((part) => {
          const node = required(record.model, part.id),
            socket = required(record.model, `socket_${part.id}`);
          batchTarget(record, node);
          return { id: part.id, node, socket, palette: materials(record, node) };
        });
      record.dolphins = new Map();
      for (const part of skyLeviathanGeometry().components.filter(
        (part) => part.kind === 'navigation',
      )) {
        const dolphin = makeDolphin(record);
        group.add(dolphin.group);
        record.dolphins.set(part.id, dolphin);
      }
      record.shafts = ['port', 'starboard'].map((side) => {
        const node = required(record.model, `fin-${side}_physical_drive_shaft`);
        node.geometry.computeBoundingBox();
        const size = node.geometry.boundingBox.getSize(new T.Vector3());
        const dimension = ['x', 'y', 'z'].sort((a, b) => size[b] - size[a])[0];
        return { node, side, dimension, length: size[dimension], scale: node.scale.clone() };
      });
      batchSkyLeviathanInterior(T, record.root, (geometry) => own(record, geometry));
      return group;
    } catch (error) {
      release(group);
      throw error;
    }
  }
  function positionInHull(group, node, local) {
    point.set(local.x, local.y, local.z).applyMatrix4(group.matrixWorld);
    node.parent.worldToLocal(point);
    node.position.copy(point);
  }
  function animate(group, item, reduced, state) {
    const record = instances.get(group),
      live = state?.leviathan;
    if (!record || !live?.systems || !live.hullFrame)
      throw new Error('Leviathan art requires the active canonical encounter snapshot.');
    const age = live.encounter.age,
      systems = live.systems,
      crashPose = Math.max(0, Math.min(1, systems.crashPose ?? 0));
    basis(group, live.hullFrame);
    sampleLining(record, systems);
    const articulation = skyLeviathanArticulation(live.encounter, systems, reduced);
    for (const joint of record.armorJoints) {
      const { x = 0, y = 0, z = 0 } = articulation[joint.id] ?? {};
      joint.node.quaternion
        .copy(joint.rest)
        .multiply(jointDelta.setFromEuler(jointRotation.set(x, y, z)));
    }
    for (const fragment of live.ruptureFragments ?? skyLeviathanRupture(systems, live.hullFrame)) {
      const node = record.fragments.get(fragment.id);
      if (!node) throw new Error(`Leviathan rupture artwork missing: ${fragment.id}`);
      basis(node, fragment.contactFrame);
    }
    const plug = live.walls.find((wall) => wall.id === 'pressure-plug');
    if (!plug) throw new Error('Leviathan pressure plug requires its canonical wall.');
    point
      .set(
        plug.center.x - record.plugClosed.x,
        plug.center.y - record.plugClosed.y,
        plug.center.z - record.plugClosed.z,
      )
      .applyMatrix4(group.matrixWorld);
    end.set(0, 0, 0).applyMatrix4(group.matrixWorld);
    record.plug.parent.worldToLocal(point);
    record.plug.parent.worldToLocal(end);
    record.plug.position.copy(record.plugBase).add(point.sub(end));
    group.updateMatrixWorld(true);
    for (const cannon of record.cannons) {
      const pose = live.cannons?.find((part) => part.side === cannon.side);
      if (!pose) throw new Error('Leviathan cannon requires its canonical physical pose.');
      point.set(pose.base.x, pose.base.y, pose.base.z);
      cannon.pivot.parent.worldToLocal(point);
      cannon.pivot.position.copy(point);
      direction
        .set(pose.direction.x, pose.direction.y, pose.direction.z)
        .transformDirection(parentInverse.copy(cannon.pivot.parent.matrixWorld).invert());
      cannon.pivot.quaternion.setFromUnitVectors(axis.set(0, 0, 1), direction);
      cannon.pivot.updateMatrixWorld(true);
    }
    for (const cannon of record.cannons) {
      cannon.socket.getWorldPosition(point);
      const origins = live.cue?.kind === 'cannon' ? (live.cue.origins ?? [live.cue.origin]) : [];
      const charging = origins.some((origin) => point.distanceToSquared(origin) < 1e-6);
      heat(cannon.palette, charging ? live.cue.charge : 0);
    }
    heat(record.tailPalette, live.tail?.charge ?? 0);
    for (const box of [...live.walls, ...live.machinery.flatMap((gate) => gate.boxes)]) {
      const binding = record.colliders.get(box.id);
      if (!binding) throw new Error(`Live collider has no physical artwork: ${box.id}`);
      positionInHull(group, binding.node, box.center);
      binding.node.scale.copy(binding.scale);
      binding.node.scale.x *= box.halfExtents.x / binding.halfX;
    }
    for (const part of record.parts) {
      const center = live.encounter.centers[part.id],
        status = live.encounter.parts[part.id];
      positionInHull(group, part.node, center);
      positionInHull(group, part.socket, center);
      const proxy = live.components.find((target) => target.componentId === part.id);
      tint(
        part.palette,
        Boolean(proxy?.vulnerable),
        status.disabled,
        part.id === 'pressure-core' && live.cue?.kind === 'pressure' ? live.cue.charge : 0,
      );
    }
    group.updateMatrixWorld(true);
    for (const shaft of record.shafts) {
      const part = record.parts.find((entry) => entry.id === `fin-${shaft.side}`);
      part.node.getWorldPosition(point);
      record.fins.find((entry) => entry.side === shaft.side).node.getWorldPosition(end);
      shaft.node.parent.worldToLocal(point);
      shaft.node.parent.worldToLocal(end);
      direction.copy(end).sub(point);
      shaft.node.position.copy(point).add(end).multiplyScalar(0.5);
      axis.set(0, 0, 0);
      axis[shaft.dimension] = 1;
      const length = direction.length();
      shaft.node.quaternion.setFromUnitVectors(axis, direction.normalize());
      shaft.node.scale.copy(shaft.scale);
      shaft.node.scale[shaft.dimension] = length / shaft.length;
    }
    // Dolphins are children for lifetime ownership, but their domain centres
    // are world coordinates. Convert through the current hull once, not twice.
    for (const dolphin of record.dolphins.values()) dolphin.group.visible = false;
    for (const target of live.dolphins) {
      const dolphin = record.dolphins.get(target.componentId);
      if (!dolphin) throw new Error(`Unknown dolphin navigation identity: ${target.componentId}`);
      dolphin.group.visible = true;
      const proxy = live.components.find((part) => part.componentId === target.componentId);
      sampleDolphin(
        dolphin,
        { ...target, vulnerable: proxy?.vulnerable },
        live.hullFrame,
        age,
        reduced,
        crashPose,
      );
    }
    group.updateMatrixWorld(true);
  }
  return {
    create,
    createDolphin,
    animate,
    animateDolphin(group, item, frame, age, reduced = false) {
      if (!instances.has(group)) throw new Error('Dolphin instance has been released.');
      sampleDolphin(group.userData.skyDolphin, item, frame, age, reduced);
    },
    release,
    dispose() {
      for (const group of [...instances.keys()]) release(group);
    },
  };
}
